/**
 * Azure AD federated logout after EONS session ends (OIDC + SAML).
 * Redirects to Microsoft oauth2/v2.0/logout so the IdP session is cleared.
 */

import { DEFAULT_SAML_PROFILE, VALID_SAML_PROFILES } from "./azureSamlSso";

export const AUTH_METHOD_STORAGE_KEY = "eons_auth_method";

const envFlagEnabled = (raw) => {
  const v = (raw || "").trim().toLowerCase();
  return v === "true" || v === "1";
};

const writeAuthMethod = (value) => {
  sessionStorage.setItem(AUTH_METHOD_STORAGE_KEY, value);
  try {
    localStorage.setItem(AUTH_METHOD_STORAGE_KEY, value);
  } catch (_) {
    /* private browsing / quota */
  }
};

const readAuthMethod = () =>
  sessionStorage.getItem(AUTH_METHOD_STORAGE_KEY) ||
  localStorage.getItem(AUTH_METHOD_STORAGE_KEY) ||
  "";

export function isAzureAdFederatedLogoutEnabled() {
  const raw = (process.env.REACT_APP_AZURE_AD_FEDERATED_LOGOUT_ENABLED || "").trim().toLowerCase();
  if (raw === "false" || raw === "0") {
    return false;
  }
  return envFlagEnabled(raw) || envFlagEnabled(process.env.REACT_APP_AZURE_SAML_SSO_ENABLED);
}

export function getAzureAdTenantId() {
  return (process.env.REACT_APP_AZURE_AD_TENANT_ID || "").trim();
}

/** @param {"oidc"} method */
export function persistOidcAuthMethod() {
  writeAuthMethod("oidc");
}

/** @param {string} profile heco | heco2 */
export function persistSamlAuthMethod(profile) {
  const key = VALID_SAML_PROFILES.includes(profile) ? profile : DEFAULT_SAML_PROFILE;
  writeAuthMethod(`saml:${key}`);
}

/** Clears auth method (e.g. password login). */
export function clearPersistedAuthMethod() {
  sessionStorage.removeItem(AUTH_METHOD_STORAGE_KEY);
  try {
    localStorage.removeItem(AUTH_METHOD_STORAGE_KEY);
  } catch (_) {
    /* ignore */
  }
}

export function getPersistedAuthMethod() {
  return readAuthMethod();
}

/** Last SAML division profile for login redirects (heco | heco2), or empty string. */
export function getLastSamlProfileForLoginRedirect() {
  const method = getPersistedAuthMethod();
  if (method.startsWith("saml:")) {
    const profile = method.slice("saml:".length) || DEFAULT_SAML_PROFILE;
    return VALID_SAML_PROFILES.includes(profile) ? profile : DEFAULT_SAML_PROFILE;
  }
  return "";
}

/** `/login` or `/login?saml=heco2` for unauthenticated redirects after SAML logout. */
export function buildLoginPathWithOptionalSaml() {
  const profile = getLastSamlProfileForLoginRedirect();
  if (profile) {
    return `/login?saml=${encodeURIComponent(profile)}`;
  }
  return "/login";
}

/** Login page URL to return to after Microsoft logout. */
export function getPostLogoutLoginRedirectUrl() {
  const method = getPersistedAuthMethod();
  const origin = window.location.origin;

  if (method.startsWith("saml:")) {
    const profile = method.slice("saml:".length) || DEFAULT_SAML_PROFILE;
    return `${origin}/login?saml=${encodeURIComponent(profile)}`;
  }
  if (method === "oidc") {
    return `${origin}/login`;
  }
  return "";
}

/**
 * Microsoft federated logout URL, or null when not applicable.
 * Reads persisted auth method from sessionStorage or localStorage before resetStore clears sessionStorage.
 */
export function buildAzureAdLogoutRedirectUrl() {
  if (!isAzureAdFederatedLogoutEnabled()) {
    return null;
  }
  const tenantId = getAzureAdTenantId();
  if (!tenantId) {
    return null;
  }
  const postLogoutRedirectUri = getPostLogoutLoginRedirectUrl();
  if (!postLogoutRedirectUri) {
    return null;
  }
  const params = new URLSearchParams({
    post_logout_redirect_uri: postLogoutRedirectUri,
  });
  return `https://login.microsoftonline.com/${encodeURIComponent(tenantId)}/oauth2/v2.0/logout?${params}`;
}

/** Preserve SAML/OIDC auth method when resetStore clears localStorage (logout flow). */
export function preserveAuthMethodDuringStoreReset() {
  return readAuthMethod();
}

export function restoreAuthMethodAfterStoreReset(preserved) {
  if (!preserved) {
    return;
  }
  try {
    localStorage.setItem(AUTH_METHOD_STORAGE_KEY, preserved);
  } catch (_) {
    /* ignore */
  }
}
